import { cn } from '@/lib/utils'
import { can as hasPermission, useSession } from '@/shared/auth/session'
import { NavLink, Outlet } from 'react-router-dom'

/**
 * Campaign finance — the tab bar.
 *
 * Same pattern as StationMatesLayout and ProgramAnalyticsLayout: the shell's sub-bar has no
 * slot, so the layout owns its own strip. A tab the caller cannot use is hidden; a tab they
 * might be refused is NOT hidden, because the server's refusal is what speaks.
 */
interface Tab {
  to: string
  label: string
  hint: string
  permission?: string
  end?: boolean
}

export const FINANCE_TABS: Tab[] = [
  {
    to: '/finance',
    label: 'Where the money went',
    hint: 'Totals, headings, places and the burn rate — and what has no receipt behind it',
    end: true,
    permission: 'finance:analyse',
  },
  {
    to: '/finance/ledger',
    label: 'The ledger',
    hint: 'Every entry, newest first. Corrections appear as their own line.',
  },
  {
    to: '/finance/prices',
    label: 'What things cost',
    hint: 'A t-shirt was 500 in August and is 750 now — the question a total cannot answer',
    permission: 'finance:analyse',
  },
  {
    to: '/finance/statement',
    label: 'Expenditure statement',
    hint: 'Every shilling by statutory heading, with the entries behind each total',
    permission: 'finance:analyse',
  },
  {
    to: '/finance/events',
    label: 'Rallies & meetings',
    hint: 'What was held, where, who came — and what it cost',
  },
]

export function FinanceLayout() {
  // `can` takes the AUTHORITIES array, not the session object — the same way
  // StationMatesLayout selects it. Passing the store itself type-checks and throws at
  // runtime inside Array.filter, which is how this was found.
  const authorities = useSession((s) => s.authorities)

  const visible = FINANCE_TABS.filter(
    (tab) => !tab.permission || hasPermission(authorities, tab.permission))

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1 border-b border-border"
           role="tablist">
        {visible.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            title={tab.hint}
            className={({ isActive }) => cn(
              'border-b-2 px-3 py-2 text-erp-sm',
              isActive
                ? 'border-foreground font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {tab.label}
          </NavLink>
        ))}
      </div>
      <Outlet />
    </div>
  )
}
